import React, { createContext, useContext, useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { checkAdminStatus, getAdminUser } from '../lib/admin-api';
import { AdminUser } from '../types/admin';

interface AdminContextType {
  isAdmin: boolean;
  adminUser: AdminUser | null;
  isLoading: boolean;
  error: string | null;
}

const AdminContext = createContext<AdminContextType | undefined>(undefined);

export function useAdmin() {
  const context = useContext(AdminContext);
  if (context === undefined) {
    throw new Error('useAdmin must be used within an AdminProvider');
  }
  return context;
}

export const AdminProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { currentUser } = useAuth();
  const [isAdmin, setIsAdmin] = useState<boolean>(false);
  const [adminUser, setAdminUser] = useState<AdminUser | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const checkAdmin = async () => {
      if (!currentUser) {
        setIsAdmin(false);
        setAdminUser(null);
        setIsLoading(false);
        return;
      }

      try {
        setIsLoading(true);
        setError(null);

        // Check if user is an admin
        const isUserAdmin = await checkAdminStatus(currentUser.uid);
        setIsAdmin(isUserAdmin);

        if (isUserAdmin) {
          // Get admin user details
          const adminUserData = await getAdminUser(currentUser.uid);
          setAdminUser(adminUserData);
        } else {
          setAdminUser(null);
        }
      } catch (error) {
        console.error('Error checking admin status:', error);
        setError('Failed to verify admin status');
        setIsAdmin(false);
        setAdminUser(null);
      } finally {
        setIsLoading(false);
      }
    };

    checkAdmin();
  }, [currentUser]);

  const value = {
    isAdmin,
    adminUser,
    isLoading,
    error
  };

  return <AdminContext.Provider value={value}>{children}</AdminContext.Provider>;
};
